import { useCallback, useEffect, useRef, useState } from 'react';
import { Howl } from 'howler';

import { useResolvedGameAudioOn } from '@/shared/hooks/useGameAudioSettings';

interface UseAudioPlayerProps {
	src?: string | string[];
	loop?: boolean;
	volume?: number;
	autoplay?: boolean;
	html5?: boolean;
	respectGlobalAudioSetting?: boolean;
	onEnd?: () => void;
}

interface AudioPlayer {
	play: () => void;
	stop: () => void;
	pause: () => void;
	fade?: (from: number, to: number, duration: number) => void;
	volume?: (vol?: number) => number | void;
	playing?: () => boolean;
}

export default function useAudioPlayer({
	src,
	loop = false,
	volume = 1.0,
	autoplay = false,
	html5 = false,
	respectGlobalAudioSetting = true,
	onEnd,
}: UseAudioPlayerProps): AudioPlayer {
	const soundRef = useRef<Howl | null>(null);
	const resumeHandlerRef = useRef<(() => void) | null>(null);
	const pendingPlayRef = useRef(false);
	const playingRef = useRef(false);
	const [, setIsPlaying] = useState(false);

	const globalAudioEnabled = useResolvedGameAudioOn();
	const audioEnabled = respectGlobalAudioSetting ? globalAudioEnabled : true;

	useEffect(() => {
		if (!audioEnabled) {
			playingRef.current = false;
			setIsPlaying(false);
			return;
		}

		const srcArray = (Array.isArray(src) ? src : src ? [src] : []).filter(Boolean) as string[];

		if (srcArray.length > 0) {
			if (typeof window !== 'undefined') {
				window.Howler.autoUnlock = true;
				window.Howler.autoSuspend = false;
			}

			const sound = new Howl({
				src: srcArray,
				loop,
				volume,
				autoplay,
				html5,
				onend: onEnd,
			});
			soundRef.current = sound;

			const detachResumeHandlers = () => {
				const resumeHandler = resumeHandlerRef.current;

				if (resumeHandler && typeof window !== 'undefined') {
					window.removeEventListener('pointerdown', resumeHandler);
					window.removeEventListener('keydown', resumeHandler);
					resumeHandlerRef.current = null;
				}
			};

			const attachResumeHandlers = () => {
				if (resumeHandlerRef.current || typeof window === 'undefined') {
					return;
				}

				const resumeHandler = () => {
					if (!sound.playing()) {
						sound.play();
					}

					detachResumeHandlers();
				};

				resumeHandlerRef.current = resumeHandler;
				window.addEventListener('pointerdown', resumeHandler, { once: true });
				window.addEventListener('keydown', resumeHandler, { once: true });
			};

			const handlePlay = () => {
				pendingPlayRef.current = false;
				playingRef.current = true;
				setIsPlaying(true);
				detachResumeHandlers();
			};

			const handlePauseOrStop = () => {
				playingRef.current = false;
				setIsPlaying(false);
			};

			sound.on('play', handlePlay);
			sound.on('pause', handlePauseOrStop);
			sound.on('stop', handlePauseOrStop);
			sound.on('end', handlePauseOrStop);
			sound.on('playerror', attachResumeHandlers);

			if (autoplay || pendingPlayRef.current) {
				pendingPlayRef.current = true;

				if (!sound.playing()) {
					sound.play();
				}
			}

			return () => {
				detachResumeHandlers();
				sound.off('play', handlePlay);
				sound.off('pause', handlePauseOrStop);
				sound.off('stop', handlePauseOrStop);
				sound.off('end', handlePauseOrStop);
				sound.off('playerror', attachResumeHandlers);
				pendingPlayRef.current = false;
				playingRef.current = false;
				setIsPlaying(false);

				if (sound.playing()) {
					sound.stop();
				}

				sound.unload();
				soundRef.current = null;
			};
		}

		soundRef.current = null;
		playingRef.current = false;
		setIsPlaying(false);
	}, [audioEnabled, autoplay, html5, loop, onEnd, src, volume]);

	const play = useCallback(() => {
		pendingPlayRef.current = true;

		if (!audioEnabled) {
			return;
		}

		const sound = soundRef.current;

		if (!sound) {
			return;
		}

		if (sound.playing()) {
			pendingPlayRef.current = false;
			return;
		}

		sound.play();
	}, [audioEnabled]);

	const stop = useCallback(() => {
		pendingPlayRef.current = false;
		const sound = soundRef.current;

		if (!sound) {
			return;
		}

		if (sound.playing()) {
			sound.stop();
		}
	}, []);

	const pause = useCallback(() => {
		pendingPlayRef.current = false;
		const sound = soundRef.current;

		if (!sound) {
			return;
		}

		if (sound.playing()) {
			sound.pause();
		}
	}, []);

	const fade = useCallback(
		(from: number, to: number, duration: number) => soundRef.current?.fade(from, to, duration),
		[],
	);

	const setVolume = useCallback((vol?: number) => {
		if (vol == null) {
			return soundRef.current?.volume();
		}

		soundRef.current?.volume(vol);
		return undefined;
	}, []);

	const isPlaying = useCallback(() => playingRef.current, []);

	return {
		play,
		stop,
		pause,
		fade,
		volume: setVolume,
		playing: isPlaying,
	};
}
